import { RouletteEvents } from '../events/roulette.js';

/** Epsilon for cross-field money reconciliation (mirrors Python 1e-6). */
const RECONCILE_EPSILON = 1e-6;

/**
 * European single-zero wheel colors. Mirrors Python `models.py:11-22`
 * (`RED_NUMBERS`/`BLACK_NUMBERS`); 0 is green. Used by `colorOf` so the TS
 * side validates `spinColor` against the number exactly as Pydantic does.
 */
export const RED_NUMBERS: ReadonlySet<number> = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);
export const BLACK_NUMBERS: ReadonlySet<number> = new Set([
  2, 4, 6, 8, 10, 11, 13, 15, 17, 20, 22, 24, 26, 28, 29, 31, 33, 35,
]);

/** Color of a roulette number on the European single-zero wheel (0 = green). */
export function colorOf(n: number): RouletteEvents.Color {
  if (n === 0) {
    return 'green';
  }
  if (RED_NUMBERS.has(n)) {
    return 'red';
  }
  return 'black';
}

/** Per-bet placement checks shared by all bet-carrying events. */
function validateBetPlacement(bet: RouletteEvents.Bet, idx: number): void {
  if (!bet || typeof bet !== 'object') {
    throw new Error(`bets[${idx}] must be an object`);
  }
  if (!RouletteEvents.BET_TYPES.includes(bet.type)) {
    throw new Error(
      `bets[${idx}].type is invalid: ${bet.type}. Must be one of: ${RouletteEvents.BET_TYPES.join(', ')}`
    );
  }
  if (!Array.isArray(bet.numbers) || bet.numbers.length === 0) {
    throw new Error(`bets[${idx}].numbers must be a non-empty array`);
  }
  for (const [numIdx, num] of bet.numbers.entries()) {
    if (typeof num !== 'number' || num < 0 || num > 36 || !Number.isInteger(num)) {
      throw new Error(
        `bets[${idx}].numbers[${numIdx}] is invalid: ${num}. Must be integer 0-36`
      );
    }
  }
  if (typeof bet.amount !== 'number' || bet.amount <= 0 || !isFinite(bet.amount)) {
    throw new Error(`bets[${idx}].amount must be a positive number`);
  }
}

export function validateRouletteBetConfirmation(
  bets: RouletteEvents.Bet[],
  totalBet: number,
  roundId: string
): void {
  if (!Array.isArray(bets) || bets.length === 0) {
    throw new Error('bets must be a non-empty array');
  }
  for (const [idx, bet] of bets.entries()) {
    validateBetPlacement(bet, idx);
  }
  if (typeof totalBet !== 'number' || totalBet <= 0 || !isFinite(totalBet)) {
    throw new Error(`Invalid totalBet: ${totalBet}. Must be a positive number`);
  }
  const sumAmount = bets.reduce((acc, bet) => acc + bet.amount, 0);
  if (Math.abs(sumAmount - totalBet) > RECONCILE_EPSILON) {
    throw new Error(`sum(bets[].amount)=${sumAmount} must equal totalBet=${totalBet}`);
  }
  if (typeof roundId !== 'string' || roundId.trim().length === 0) {
    throw new Error('roundId must be a non-empty string');
  }
}

/**
 * `bets_update` variant: full live-table snapshot, so an empty array is a
 * valid state (cleared table) and totalBet may be 0. Per-bet checks are
 * identical to bet_confirmation; there is no roundId during the betting
 * window.
 */
export function validateRouletteBetsUpdate(bets: RouletteEvents.Bet[], totalBet: number): void {
  if (!Array.isArray(bets)) {
    throw new Error('bets must be an array');
  }
  for (const [idx, bet] of bets.entries()) {
    validateBetPlacement(bet, idx);
  }
  if (typeof totalBet !== 'number' || totalBet < 0 || !isFinite(totalBet)) {
    throw new Error(`Invalid totalBet: ${totalBet}. Must be a non-negative number`);
  }
  const sumAmount = bets.reduce((acc, bet) => acc + bet.amount, 0);
  if (Math.abs(sumAmount - totalBet) > RECONCILE_EPSILON) {
    throw new Error(`sum(bets[].amount)=${sumAmount} must equal totalBet=${totalBet}`);
  }
}

export interface RouletteGameOverInput {
  spinNumber: number;
  spinColor: RouletteEvents.Color;
  bets: RouletteEvents.BetOutcome[];
  totalBet: number;
  totalWin: number;
  roundId: string;
}

export function validateRouletteGameOver(input: RouletteGameOverInput): void {
  const { spinNumber, spinColor, bets, totalBet, totalWin, roundId } = input;
  if (
    typeof spinNumber !== 'number' ||
    spinNumber < 0 ||
    spinNumber > 36 ||
    !Number.isInteger(spinNumber)
  ) {
    throw new Error(`Invalid spinNumber: ${spinNumber}. Must be integer 0-36`);
  }
  if (!RouletteEvents.COLORS.includes(spinColor)) {
    throw new Error(
      `Invalid spinColor: ${spinColor}. Must be one of: ${RouletteEvents.COLORS.join(', ')}`
    );
  }
  if (!Array.isArray(bets) || bets.length === 0) {
    throw new Error('bets must be a non-empty array');
  }
  for (const [idx, bet] of bets.entries()) {
    validateBetPlacement(bet, idx);
    if (typeof bet.won !== 'boolean') {
      throw new Error(`bets[${idx}].won must be a boolean`);
    }
    // GROSS return: stake + profit, never negative; 0 if lost.
    if (typeof bet.payout !== 'number' || bet.payout < 0 || !isFinite(bet.payout)) {
      throw new Error(`bets[${idx}].payout must be a non-negative number`);
    }
    // Verdict is carried by `won`; it must agree with gross payout > 0.
    if (bet.won !== bet.payout > 0) {
      throw new Error(
        `bets[${idx}].won=${bet.won} contradicts payout=${bet.payout} (won must equal payout > 0)`
      );
    }
  }
  if (typeof totalBet !== 'number' || totalBet <= 0 || !isFinite(totalBet)) {
    throw new Error(`Invalid totalBet: ${totalBet}. Must be a positive number`);
  }
  if (typeof totalWin !== 'number' || totalWin < 0 || !isFinite(totalWin)) {
    throw new Error(`Invalid totalWin: ${totalWin}. Must be a non-negative number`);
  }
  // Cross-field: spinColor must match the color derived from spinNumber.
  const derivedColor = colorOf(spinNumber);
  if (spinColor !== derivedColor) {
    throw new Error(
      `spinColor=${spinColor} contradicts derived color ${derivedColor} for spinNumber=${spinNumber}`
    );
  }
  const sumAmount = bets.reduce((acc, bet) => acc + bet.amount, 0);
  if (Math.abs(sumAmount - totalBet) > RECONCILE_EPSILON) {
    throw new Error(`sum(bets[].amount)=${sumAmount} must equal totalBet=${totalBet}`);
  }
  const sumPayout = bets.reduce((acc, bet) => acc + bet.payout, 0);
  if (Math.abs(sumPayout - totalWin) > RECONCILE_EPSILON) {
    throw new Error(`sum(bets[].payout)=${sumPayout} must equal totalWin=${totalWin}`);
  }
  if (typeof roundId !== 'string' || roundId.trim().length === 0) {
    throw new Error('roundId must be a non-empty string');
  }
}
